import { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import type { User } from 'firebase/auth';
import { Chat } from './components/chat/Chat.tsx';
import { WorkPanel } from './components/layout/WorkPanel.tsx';
import { Sidebar } from './components/layout/Sidebar.tsx';
import { SignIn } from './components/layout/SignIn.tsx';
import { CommandCenter } from './routes/CommandCenter.tsx';
import { IconMenu, IconPanelRight, Mark } from './components/ui/Icons.tsx';
import { useRun } from './lib/useRun.ts';
import { watchAuth } from './lib/firebase.ts';
import { useIsMobile, useRailOverlays } from './lib/useMediaQuery.ts';

const EASE = [0.22, 1, 0.36, 1] as const;

function Workspace({
  railOpen,
  setRailOpen,
  showMenu,
  onMenu,
}: {
  railOpen: boolean;
  setRailOpen: (v: boolean) => void;
  showMenu: boolean;
  onMenu: () => void;
}) {
  const threadId = useMemo(() => crypto.randomUUID(), []);
  const { items, tools, timeline, conn, send, decide, abort } = useRun(threadId);
  const railOverlays = useRailOverlays();
  const running = tools.filter((t) => t.ok === undefined).length;

  return (
    <>
      <div className="lx-col">
        <TopBar
          show={showMenu}
          onMenu={onMenu}
          onRail={() => setRailOpen(!railOpen)}
          railOpen={railOpen}
          badge={running}
        />
        <Chat items={items} conn={conn} onSend={send} onDecide={decide} onStop={abort} />
      </div>

      {/* Above 1100px the rail is a real column and always present. Below it, it overlays —
          holding it as a fixed column is what squeezed the chat to zero width on a phone. */}
      {railOverlays ? (
        <AnimatePresence>
          {railOpen && (
            <>
              <motion.button
                className="lx-scrim"
                aria-label="Close activity panel"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setRailOpen(false)}
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration: 0.32, ease: EASE }}
                style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 40 }}
              >
                <WorkPanel tools={tools} timeline={timeline} onClose={() => setRailOpen(false)} />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      ) : (
        <WorkPanel tools={tools} timeline={timeline} />
      )}
    </>
  );
}

function TopBar({
  show,
  onMenu,
  onRail,
  railOpen,
  badge,
}: {
  show: boolean;
  onMenu: () => void;
  onRail: () => void;
  railOpen: boolean;
  badge: number;
}) {
  const railOverlays = useRailOverlays();
  if (!show && !railOverlays) return null;
  return (
    <header className="lx-topbar">
      {show && (
        <button className="lx-icon-btn lx-focus" onClick={onMenu} aria-label="Open menu">
          <IconMenu />
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <Mark size={22} />
        <strong style={{ fontSize: 15, letterSpacing: '-0.02em' }}>Lumlix</strong>
      </div>
      {railOverlays && (
        <button
          className="lx-icon-btn lx-focus"
          onClick={onRail}
          data-on={railOpen}
          aria-label="Toggle activity panel"
        >
          <IconPanelRight />
          {badge > 0 && <span className="lx-badge">{badge}</span>}
        </button>
      )}
    </header>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [navOpen, setNavOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();

  useEffect(() => watchAuth(setUser), []);

  // Navigating is an implicit "done with the menu" — leaving the drawer open over the new
  // route is the classic mobile-nav annoyance.
  useEffect(() => {
    setNavOpen(false);
    setRailOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen && !railOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNavOpen(false);
        setRailOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen, railOpen]);

  if (user === undefined) return <Splash />;
  if (user === null) return <SignIn />;

  return (
    <div className="lx-app">
      {isMobile ? (
        <AnimatePresence>
          {navOpen && (
            <>
              <motion.button
                className="lx-scrim"
                aria-label="Close menu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setNavOpen(false)}
              />
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ duration: 0.32, ease: EASE }}
                style={{ position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 40 }}
              >
                <Sidebar user={user} onClose={() => setNavOpen(false)} />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      ) : (
        <Sidebar user={user} />
      )}

      <main className="lx-main">
        <Routes>
          <Route
            path="/"
            element={
              <Workspace
                railOpen={railOpen}
                setRailOpen={setRailOpen}
                showMenu={isMobile}
                onMenu={() => setNavOpen(true)}
              />
            }
          />
          <Route
            path="/command-center"
            element={
              <div className="lx-col">
                {isMobile && (
                  <header className="lx-topbar">
                    <button className="lx-icon-btn lx-focus" onClick={() => setNavOpen(true)} aria-label="Open menu">
                      <IconMenu />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Mark size={22} />
                      <strong style={{ fontSize: 15, letterSpacing: '-0.02em' }}>Lumlix</strong>
                    </div>
                  </header>
                )}
                <CommandCenter />
              </div>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

function Splash() {
  return (
    <div className="lx-auth">
      <div className="lx-aurora" />
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        style={{ position: 'relative', zIndex: 1, display: 'grid', placeItems: 'center', gap: 14 }}
      >
        <Mark size={44} />
        <span className="lx-thinking" style={{ fontSize: 15, fontWeight: 600, letterSpacing: '0.02em' }}>
          Loading your workspace
        </span>
      </motion.div>
    </div>
  );
}
